import { ClientType, type Tokens } from '@/generated/api'
import { Token } from './Token'
import { randomString } from '../random'
import { callbackNewAccessToken } from './callbacks'
import { MobileMissionsApiClient } from '@/generated/api/MobileMissionsApiClient'
import { getEnv } from '../env'
import { isRefreshTokenAlreadyUsedError, isRefreshTokenExpiredError } from '../responseError'
import type { Capabilities } from './capabilities'
import { convertToBoolean } from '../string'
import { getDeviceApiWebValues } from './deviceApiWebValues'
import { LocalStorageItem } from '../localStorage'
import log from 'loglevel'
import { resultError, resultOk, type Result } from '../result'
import type { DeviceApiClientName } from './clientName'

export class ApiNotSupportedError extends Error {
  constructor(methodName: string) {
    super()
    this.message = `Tried to use client API method '${methodName}' unsupported by web implementation.`
  }
}

/**
 * Used when accessing Opera Rewards from:
 * - a web browser during development,
 * - or from unsupported browsers on production.
 */
export class DeviceApiWeb {
  readonly type = 'web'

  protected accessToken: Token | null = null
  protected refreshToken: Token | null = null
  private apiClient: MobileMissionsApiClient

  constructor(
    apiBaseUrl: string = getEnv('apiBaseUrl'),
    private accessTokenStorage = new LocalStorageItem('omm-accessToken'),
    private refreshTokenStorage = new LocalStorageItem('omm-refreshToken'),
    private clientValues = getDeviceApiWebValues()
  ) {
    log.debug({ clientName: clientValues.clientName.get(), env: getEnv('deviceApiWebClientName') })
    this.apiClient = new MobileMissionsApiClient({ BASE: apiBaseUrl })
    this.setTokensFromLocalStorageIfValid()
  }

  private setTokensFromLocalStorageIfValid() {
    const accessTokenRaw = this.accessTokenStorage.get()
    if (accessTokenRaw === null) return
    const accessToken = Token.parseOrNull(accessTokenRaw)
    if (accessToken === null) return

    const refreshTokenRaw = this.refreshTokenStorage.get()
    if (refreshTokenRaw === null) return
    const refreshToken = Token.parseOrNull(refreshTokenRaw)
    if (refreshToken === null) return

    this.accessToken = accessToken
    this.refreshToken = refreshToken
  }

  getClientName(): DeviceApiClientName {
    return (this.clientValues.clientName.get() ??
      getEnv('deviceApiWebClientName')) as DeviceApiClientName
  }

  getClientNameAsResult(): Result<DeviceApiClientName> {
    try {
      return resultOk(this.getClientName())
    } catch (error: unknown) {
      return resultError(error)
    }
  }

  getClientLocalization(): string {
    return this.clientValues.clientLocalization.get() ?? 'ng'
  }

  getClientVersion(): string {
    return this.clientValues.clientVersion.get() ?? getEnv('deviceApiWebClientVersion')
  }

  getCapabilities(): Capabilities {
    return { supportsDefaultBrowser: true }
  }

  getApiKey(clientType: ClientType): string {
    switch (clientType) {
      case ClientType.OFA:
        return getEnv('testingApiKeyOfA')
      case ClientType.MINI:
        return getEnv('testingApiKeyMini')
      default:
        return ''
    }
  }

  private getReferrerId(): string | undefined {
    const url = new URL(window.location.href)
    const referrerId = url.searchParams.get('referrer_id')
    return referrerId ?? undefined
  }

  private async apiCreateAnonUser(): Promise<Tokens> {
    const clientType = this.getClientName() as ClientType

    return this.apiClient.auth.anonymousLogin({
      xApiKey: this.getApiKey(clientType) || undefined,
      requestBody: {
        haid: this.clientValues.haid.get() ?? randomString(),
        huid: this.clientValues.huid.get(),
        // TODO(pszyma): add runtime type check.
        clientType: this.getClientName() as ClientType,
        clientVersion: this.getClientVersion(),
        userCountry: this.getClientLocalization()
      }
    })
  }

  private async apiRefreshTokens(refreshToken: Token): Promise<Tokens> {
    try {
      return await this.apiClient.auth.tokensFromRefreshToken({
        requestBody: {
          refreshToken: refreshToken.raw,
          huid: this.clientValues.huid.get()
        }
      })
    } catch (error: unknown) {
      if (isRefreshTokenExpiredError(error) || isRefreshTokenAlreadyUsedError(error)) {
        return this.apiCreateAnonUser()
      } else {
        throw error
      }
    }
  }

  private parseAndUpdateTokens(apiTokens: Tokens): Token {
    const accessToken = new Token(apiTokens.accessToken)
    const refreshToken = new Token(apiTokens.refreshToken)

    this.accessToken = accessToken
    this.accessTokenStorage.set(this.accessToken.raw)

    this.refreshToken = refreshToken
    this.refreshTokenStorage.set(this.refreshToken.raw)

    return accessToken
  }

  private async requestAccessTokenInternal(forceRefresh: boolean): Promise<Token> {
    if (this.accessToken === null) {
      return this.parseAndUpdateTokens(await this.apiCreateAnonUser())
    }

    if (forceRefresh) {
      if (this.refreshToken === null) {
        return this.parseAndUpdateTokens(await this.apiCreateAnonUser())
      } else {
        return this.parseAndUpdateTokens(await this.apiRefreshTokens(this.refreshToken))
      }
    }

    // This code simulates the behavior of a native client when its
    // version changes (e.g., due to an update or downgrade).
    //
    // It detects a mismatch between the client version in the JWT token claims
    // and the actual version on the client. If a mismatch is found and a
    // refresh token is available, it triggers a token refresh.
    //
    // It's important to note that the token refresh process sends the
    // current client version to the backend. This causes the backend to:
    //   - Generate new JWT claims with the updated version.
    //   - Update the client version in the database (UserClient model).
    //
    const clientVersionOnClient = this.getClientVersion()
    const clientVersionInClaims = this.accessToken.claims['client_version']

    if (
      this.refreshToken !== null &&
      clientVersionInClaims &&
      clientVersionOnClient != clientVersionInClaims
    ) {
      log.debug(
        'mismatch between version on server & client detected, versionOnClient = ',
        clientVersionOnClient,
        'versionOnServer = ',
        clientVersionInClaims,
        'triggering token refresh which will cause user client & claims to be updated'
      )
      return this.parseAndUpdateTokens(await this.apiRefreshTokens(this.refreshToken))
    }

    return this.accessToken
  }

  requestAccessToken(forceRefresh: boolean): void {
    const callbackFn = callbackNewAccessToken.get()

    this.requestAccessTokenInternal(forceRefresh)
      .then((token) => {
        callbackFn?.(token.raw)
      })
      .catch((error) => {
        log.error(error)
        callbackFn?.(null)
      })
  }

  onTokensChange(accessToken: string, refreshToken: string): void {
    this.parseAndUpdateTokens({ accessToken, refreshToken })
  }

  onLogout(): void {
    this.refreshTokenStorage.remove()
    this.refreshToken = null

    this.accessTokenStorage.remove()
    this.accessToken = null
  }

  getWalletAddress(): string | undefined {
    const walletAddress = this.clientValues.walletAddress.get()

    // When the value is empty string then most probably it was set manually
    // via local storage during development - treat empty string as `undefined`
    // representing that no wallet address was set.
    if (walletAddress === '') {
      return undefined
    }

    return walletAddress ?? undefined
  }

  openOperaMiniDeepLink(_deepLink: string, _close: boolean): void {
    throw new ApiNotSupportedError(`openOperaMiniDeepLink("${_deepLink}", ${_close})`)
  }

  close(): void {
    throw new ApiNotSupportedError('close')
  }

  requestSocialLogin(): void {
    throw new ApiNotSupportedError('requestSocialLogin')
  }

  getSocialAccount(): string | undefined {
    throw new ApiNotSupportedError('getSocialAccount')
  }

  socialSignOut(): void {
    throw new ApiNotSupportedError('socialSignOut')
  }

  async isDefaultBrowser(): Promise<boolean> {
    const isDefaultRaw = this.clientValues.isDefaultBrowser.get()
    if (isDefaultRaw === null) return Promise.resolve(false)
    return Promise.resolve(convertToBoolean(isDefaultRaw))
  }

  getKilobytesSavedToday(): number {
    const rawValue = this.clientValues.kilobytesSavedToday.get() ?? '0'

    return Number(rawValue)
  }

  getKilobytesSavedTodayAsResult(): Result<number> {
    try {
      return resultOk(this.getKilobytesSavedToday())
    } catch (error: unknown) {
      return resultError(error)
    }
  }

  triggerDataSavedReport(): void {
    throw new ApiNotSupportedError('triggerDataSavedReport')
  }

  getAccessToken(_forceRefresh: Boolean): Promise<string> {
    throw new ApiNotSupportedError('getAccessToken')
  }
}
